import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";

import { settingsAjax } from "../../ajax/SettingsAjax";
import { setShiftBoundaries, setShiftEditability, setShiftEditabilityDataLoading } from "../../state/reducers/shiftReducer";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { useTimezone } from "../Providers/TimezoneProvider";
import { RootState } from "../../state/store";

export default function useShiftBoundarySync() {
  const dispatch = useDispatch();
  const { timezone, isTimezoneLoading } = useTimezone();
  const selectedDateStr = useSelector((state: RootState) => state.shift.selectedDate);
  const selectedShift = useSelector((state: RootState) => state.shift.selectedShift);
  const [shiftBoundaryFetchError, setShiftBoundaryFetchError] = useState<string | null>(null);
  const [shiftEditabilityFetchError, setShiftEditabilityFetchError] = useState<string | null>(null);

  useEffect(() => {
    // Note: Wait until timezone is fully loaded — without it, selectedDate would use a UTC
    // fallback and the effect would fire a second time when the real timezone arrives,
    // dispatching setShiftEditability twice and flashing the lock banner.
    if (!selectedDateStr || !selectedShift || isTimezoneLoading || !timezone) return;

    const selectedDate = DateTime.fromISO(selectedDateStr)
      .toLocal()
      .setZone(timezone, { keepLocalTime: true })
      .startOf("day");
    const backendCode = selectedShift === "DAY" ? "D" : "N";

    setShiftBoundaryFetchError(null);
    dispatch(setShiftEditabilityDataLoading(true));

    settingsAjax.getShiftBoundaries(selectedDate, backendCode)
      .then(res => {
        if (!ErrorResponse.isErrorResponse(res)) {
          dispatch(setShiftBoundaries({
            start: res.shiftStart.toISO()!,
            end: res.shiftEnd.toISO()!
          }));
          setShiftBoundaryFetchError(null);
        } else {
          setShiftBoundaryFetchError(res.messages[0].description);
          console.error("Failed to fetch shift boundaries", res);
        }
      })
      .catch(error => {
        if (error !== "CANCELLED") {
          setShiftBoundaryFetchError("Failed to fetch shift boundaries");
          console.error("Failed to fetch shift boundaries", error);
        }
      })
      .then(() => settingsAjax.getShiftEditability(selectedDate, backendCode)
        .then(res => {
          if (!ErrorResponse.isErrorResponse(res)) {
            dispatch(setShiftEditability(res));
            setShiftEditabilityFetchError(null);
          } else {
            setShiftEditabilityFetchError(res.messages[0].description);
            console.error("Failed to fetch shift editability", res);
          }
        })
        .catch(() => {
          setShiftEditabilityFetchError("Failed to fetch shift editability");
          dispatch(setShiftEditability({
            isLocked: true,
            canToggleLock: false,
            isPastAutoLockCutOff: null
          }));
        })
      )
      .finally(() => {
        dispatch(setShiftEditabilityDataLoading(false));
      });
  }, [dispatch, selectedDateStr, selectedShift, isTimezoneLoading, timezone]);

  return { isTimezoneLoading, shiftBoundaryFetchError, shiftEditabilityFetchError };
}
